import type {
  CreateImagFaseInput,
  CreateImagFuncionalidadeInput,
  CreateImagSolucaoInput,
  ImagAuditLog,
  ImagFase,
  ImagFuncionalidade,
  ImagSolucao,
  ImagSolucaoWithCounts,
  ReorderFuncionalidadesInput,
  UpdateImagFaseInput,
  UpdateImagFuncionalidadeInput,
  UpdateImagSolucaoInput,
} from '@internal/orquestra-contract';
import { OrquestraProcessToBeClient } from './OrquestraProcessToBeClient';
import type { ImagineAuditFilter } from './OrquestraApi.imagine.types';

function solucoes(projectId: number): string {
  return `/projects/${projectId}/imagine/solucoes`;
}

function fases(projectId: number, solucaoId: number): string {
  return `${solucoes(projectId)}/${solucaoId}/fases`;
}

function funcs(projectId: number, solucaoId: number): string {
  return `${solucoes(projectId)}/${solucaoId}/funcionalidades`;
}

/**
 * Estende `OrquestraProcessToBeClient` com os métodos da hierarquia base do
 * Imagine (Fase 4.3a). Classe intermediária — o client concreto que implementa o
 * `OrquestraApi` completo é o `OrquestraImagineDetailClient` (mais derivado, com os
 * detalhes da 4.3b) — split para manter cada arquivo sob 300 linhas.
 */
export class OrquestraImagineClient extends OrquestraProcessToBeClient {
  async listSolucoes(projectId: number): Promise<ImagSolucaoWithCounts[]> {
    return this.request<ImagSolucaoWithCounts[]>(solucoes(projectId));
  }

  async getSolucao(projectId: number, solucaoId: number): Promise<ImagSolucao> {
    return this.request<ImagSolucao>(`${solucoes(projectId)}/${solucaoId}`);
  }

  async createSolucao(
    projectId: number,
    input: CreateImagSolucaoInput,
  ): Promise<ImagSolucao> {
    return this.request<ImagSolucao>(
      solucoes(projectId),
      this.jsonInit('POST', input),
    );
  }

  async updateSolucao(
    projectId: number,
    solucaoId: number,
    input: UpdateImagSolucaoInput,
  ): Promise<ImagSolucao> {
    return this.request<ImagSolucao>(
      `${solucoes(projectId)}/${solucaoId}`,
      this.jsonInit('PUT', input),
    );
  }

  async deleteSolucao(
    projectId: number,
    solucaoId: number,
    reason?: string | null,
  ): Promise<void> {
    await this.request(
      `${solucoes(projectId)}/${solucaoId}`,
      this.jsonInit('DELETE', { reason }),
    );
  }

  async listFases(projectId: number, solucaoId: number): Promise<ImagFase[]> {
    return this.request<ImagFase[]>(fases(projectId, solucaoId));
  }

  async createFase(
    projectId: number,
    solucaoId: number,
    input: CreateImagFaseInput,
  ): Promise<ImagFase> {
    return this.request<ImagFase>(
      fases(projectId, solucaoId),
      this.jsonInit('POST', input),
    );
  }

  async updateFase(
    projectId: number,
    solucaoId: number,
    faseId: number,
    input: UpdateImagFaseInput,
  ): Promise<ImagFase> {
    return this.request<ImagFase>(
      `${fases(projectId, solucaoId)}/${faseId}`,
      this.jsonInit('PUT', input),
    );
  }

  async deleteFase(
    projectId: number,
    solucaoId: number,
    faseId: number,
    reason?: string | null,
  ): Promise<void> {
    await this.request(
      `${fases(projectId, solucaoId)}/${faseId}`,
      this.jsonInit('DELETE', { reason }),
    );
  }

  async listFuncionalidades(
    projectId: number,
    solucaoId: number,
  ): Promise<ImagFuncionalidade[]> {
    return this.request<ImagFuncionalidade[]>(funcs(projectId, solucaoId));
  }

  async createFuncionalidade(
    projectId: number,
    solucaoId: number,
    input: CreateImagFuncionalidadeInput,
  ): Promise<ImagFuncionalidade> {
    return this.request<ImagFuncionalidade>(
      funcs(projectId, solucaoId),
      this.jsonInit('POST', input),
    );
  }

  async updateFuncionalidade(
    projectId: number,
    solucaoId: number,
    funcionalidadeId: number,
    input: UpdateImagFuncionalidadeInput,
  ): Promise<ImagFuncionalidade> {
    return this.request<ImagFuncionalidade>(
      `${funcs(projectId, solucaoId)}/${funcionalidadeId}`,
      this.jsonInit('PUT', input),
    );
  }

  async deleteFuncionalidade(
    projectId: number,
    solucaoId: number,
    funcionalidadeId: number,
    reason?: string | null,
  ): Promise<void> {
    await this.request(
      `${funcs(projectId, solucaoId)}/${funcionalidadeId}`,
      this.jsonInit('DELETE', { reason }),
    );
  }

  async reorderFuncionalidades(
    projectId: number,
    solucaoId: number,
    input: ReorderFuncionalidadesInput,
  ): Promise<ImagFuncionalidade[]> {
    return this.request<ImagFuncionalidade[]>(
      `${funcs(projectId, solucaoId)}/reorder`,
      this.jsonInit('POST', input),
    );
  }

  async getImagineAuditLog(
    projectId: number,
    filter: ImagineAuditFilter = {},
  ): Promise<ImagAuditLog[]> {
    const params = new URLSearchParams();
    if (filter.entityType) params.set('entityType', filter.entityType);
    if (filter.entityId !== undefined) {
      params.set('entityId', String(filter.entityId));
    }
    const qs = params.toString();
    return this.request<ImagAuditLog[]>(
      `/projects/${projectId}/imagine/audit-log${qs ? `?${qs}` : ''}`,
    );
  }
}
